/**
 * Dictation anywhere's prompt names and keys in Settings captions, in the
 * rendered language (call these at render time): the start/stop shortcut
 * (the user's own binding) and the fixed prompt keys, Command+1 to Command+9
 * on macOS and Windows+1 to Windows+9 on Windows. The caps are drawn by
 * `voice-dictation-key-caps.tsx`.
 */
import { translate } from "@/i18n";
import {
  PROMPT_CHIP_LABEL_KEY,
  settingsVoiceMessages,
  TRANSLATE_LANGUAGE_KEY,
} from "@/i18n/messages/settings-voice";
import { DEFAULT_PROMPT_NAME } from "@/desktop/dictation-anywhere/polish-prompts";
import {
  chipsIn,
  translateLanguageIn,
} from "@/desktop/dictation-anywhere/prompt-chips";
import {
  describeShortcut,
  SHORTCUT_REGISTRY,
  type ShortcutId,
} from "@/lib/shortcut-registry";
import {
  bindingKeyCaps,
  getShortcutBinding,
  promptStopBinding,
  shortcutKeyCap,
} from "@/lib/shortcuts";
import { useShortcutStore } from "@/stores/shortcut-store";

export const DICTATION_SHORTCUT_ID: ShortcutId = "shortcut.dictation.anywhere";
export const DICTATION_SHORTCUT = SHORTCUT_REGISTRY.find(
  (item) => item.id === DICTATION_SHORTCUT_ID,
)!;

/** Re-renders when the start/stop binding changes; the helpers below read
 *  the shortcut store directly. */
export function useDictationShortcutBinding(): void {
  useShortcutStore((state) => state.overrides[DICTATION_SHORTCUT_ID]);
}

/** How the start/stop shortcut is used: "Double-tap ⌘",
 *  "Press Windows + Shift". In the rendered language; call it at render time. */
export function startStopPhrase(): string {
  const binding = getShortcutBinding(DICTATION_SHORTCUT_ID);
  if (binding.holdKeys?.length) return describeShortcut(binding);
  if (binding.gesture === "doubleTap") {
    return translate(settingsVoiceMessages, "doubleTapKeys", {
      keys: binding.keys.map(shortcutKeyCap).join(" + "),
    });
  }
  if (binding.gesture === "chord") {
    return translate(settingsVoiceMessages, "pressKeys", {
      keys: bindingKeyCaps(binding).join(""),
    });
  }
  return describeShortcut(binding);
}

/** How prompt `number` is used: "Press ⌘1", "Press Windows + 1". */
export function promptKeyPhrase(number: number): string {
  return translate(settingsVoiceMessages, "pressKeys", {
    keys: bindingKeyCaps(promptStopBinding(number)).join(""),
  });
}

/** The prompt keys' modifier in a caption: "⌘" or "Windows". */
export function promptKeyModifier(): string {
  return shortcutKeyCap("Meta");
}

/** A numbered prompt's name: its own, else "Prompt N". */
export function promptLabel(prompt: { name: string }, index: number): string {
  return (
    prompt.name.trim() ||
    translate(settingsVoiceMessages, "promptNumber", { number: index + 1 })
  );
}

/** A prompt name a dictation was stopped with; Default reads translated. */
export function stopPromptName(name: string): string {
  return name === DEFAULT_PROMPT_NAME
    ? translate(settingsVoiceMessages, "promptDefault")
    : name;
}

/** A Translate language by its English name; one typed by hand stays as is. */
export function translateLanguageName(language: string): string {
  const key = TRANSLATE_LANGUAGE_KEY[language];
  return key ? translate(settingsVoiceMessages, key) : language;
}

/** A prompt's second line: its chips, or what it is without. */
export function promptChipsSummary(instruction: string): string {
  const parts = chipsIn(instruction).map((id) =>
    id === "translate"
      ? translate(settingsVoiceMessages, "toLanguage", {
          language: translateLanguageName(
            translateLanguageIn(instruction) ?? "",
          ),
        })
      : translate(settingsVoiceMessages, PROMPT_CHIP_LABEL_KEY[id]),
  );
  if (parts.length > 0)
    return parts.join(translate(settingsVoiceMessages, "summarySeparator"));
  return translate(
    settingsVoiceMessages,
    instruction.trim() ? "summaryCustom" : "summaryAsSpoken",
  );
}
